import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex } from "@chakra-ui/react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { pdf } from "@react-pdf/renderer";
import { datadogLogs } from "@datadog/browser-logs";

import { useP2p, useChat } from "./hooks";
import { getPeersConnectionType } from "./helpers";

import useStore from "@/stores/index";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

import { userDisplayText } from "@/js/utils/userUtils";
import useClickOutside from "@/js/utils/outsideClickUtils";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";

import Toggle from "@/js/components/Toggle";
import { ChatButton } from "@/js/components/ClassroomChat/components/ChatButton";
import P2PTabsBar from "./components/TabBar";
import DataChannelWrapper from "./DataChannelWrapper";
import P2PScreenWrapper from "./components/P2pScreenWrapper";
import ChatWindow from "@/js/components/ClassroomChat/components/ChatWindow/index";
import PDFSnapshotDocument from "./components/PDFSnapshot/PDFSnapshotDocument";
import ConfirmScreenshotPopup from "./components/PDFSnapshot/ConfirmScreenshotPopup";
import { ActionButton } from "@/js/components/Classrooms/ActionButton";
import SendMessageModal from "@/js/components/SendMessageModal";

import CloseFullscreenIcon from "@/assets/closeFullscreenIcon.svg?url";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { qoriaColours } from "@/theme/colours";
import { ContextBasedP2PWindow } from "@/js/components/P2PDisplayModal/components/ContextBasedP2PWindow";
import { ClassroomStudentActivityState } from "js/components/RegularClassroomUser/utils";
import { convertWebsiteToSignature } from "@/utils/convertWebsiteToSignature";

const SCREENSHOT_ACCEPT_LOCALSTORAGE_KEY = "PDF_SNAPSHOT_ACCEPT_TIME";

// error messages
//! Should be conditionally rendered inside parent as this component requires some setup logic that uses hooks (rules of hooks states to not use hooks conditionally)
const P2P_ERROR_KEYS = new Set(["connectionFailed", "connectionClosedUnexpectedly", "datachannelClosed"]);

const P2PDisplayModal = (props) => {
	const { t } = useTranslation();
	const imgRef = useRef();
	const [imgDataUri, setImgDataUri] = useState();
	const [screenshotAvailable, setScreenshotAvailable] = useState(false);
	const [screenshotState, setScreenshotState] = useState(ClassroomStudentActivityState.UNSET);
	const [screenshottable, setScreenshottable] = useState(false);

	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [followActiveTab, setFollowActiveTab] = useState(false);

	const [errorLabel, setErrorLabel] = useState();

	const [currentTab, setCurrentTab] = useState(props.startTab);
	const [currentTabIndex, setCurrentTabIndex] = useState(props.startTabIndex);
	const [tabs, setTabs] = useState(props.tabs);
	const [activeTabIndex, setActiveTabIndex] = useState(0);

	const [showChat, toggleShowChat] = useState(false);
	const [sendMessageModalOpen, setSendMessageModalOpen] = useState(false);

	useEffect(() => {
		if (!props.open) {
			setSendMessageModalOpen(false);
		}
	}, [props.open]);

	const classroomSourceId = useUserSessionStore((state) => state.getActiveGroup());
	const sendMessageModalTitle = useMemo(() => t("classroomDetails.sendMessageTo", { name: userDisplayText(props.user) }), [props.user, t]);

	const liveChatWrapperRef = useRef();
	const chatButtonWrapperRef = useRef();

	const chat = useChat(props.classroom, props.user);

	const features = getFeatures(useStore.getState());
	const dataChannelRef = useRef();
	const controlDataChannelRef = useRef();

	const controlDataChannelCloseRef = useRef();
	const dataChannelCloseRef = useRef();

	const currentTabRef = useRef(currentTab);
	const tabsRef = useRef(tabs);
	const currentTabIndexRef = useRef(currentTabIndex);
	const followActiveTabRef = useRef(followActiveTab);

	const [snapshotDialogueOpen, setSnapshotDialogueOpen] = useState(false);
	const [storedSnapshot, setStoredSnapshot] = useState();
	const isUserPaused = useClassroomPolicyStore.getState().getPausedStudents().includes(props.user.username);

	const debugStartTimestampRef = useRef(Date.now());
	const debugFirstConnectedTimestampRef = useRef();
	const debugFirstScreenshotDataChannelOpenTimestampRef = useRef();
	const debugFirstScreenshotReceivedTimestampRef = useRef();
	const debugFirstControlDataChannelOpenTimestampRef = useRef();
	const debugFirstConnectedTypeRef = useRef();

	const p2p = useP2p(props.open, props.peerAgentId, props.user, "chromebook", (message) => {
		switch (message) {
			case "STUDENT_NOT_SIGNALED":
				console.info(`Extension Live View - student not connected to signaler after 5 seconds`);
				datadogLogs.logger.log(`Extension Live View - student not connected to signaler after 5 seconds`);
				break;
			case "CONNECTION_NOT_ESTABLISHED":
				console.info(`Extension Live View - connection not established after 10 seconds`);
				datadogLogs.logger.log(`Extension Live View - connection not established after 10 seconds`);
				break;
			default:
				console.error("Extension Live View - connection is taking a long time...");
		}
	});

	useEffect(() => {
		console.info("Extension Live View - start");
		datadogLogs.logger.info("Extension Live View - start");
	}, []);

	const cleanup = () => {
		if (dataChannelCloseRef.current) {
			dataChannelCloseRef.current();
		}
		if (controlDataChannelCloseRef.current) {
			controlDataChannelCloseRef.current();
		}
		if (debugStartTimestampRef.current && !debugFirstScreenshotReceivedTimestampRef.current) {
			// The screenshots not received yet
			const now = Date.now();
			const deltaStartSeconds = (now - debugStartTimestampRef.current) / 1000;
			console.info(`Extension Live View - teacher closed live view before screenshot received, after ${deltaStartSeconds} seconds`);
			datadogLogs.logger.log(
				`Extension Live View - teacher closed live view before screenshot received, after ${deltaStartSeconds} seconds`,
				{
					sinceStart: deltaStartSeconds,
				}
			);
		}
	};

	const disabledChat = useMemo(() => {
		const classroomChatDisabled = props.chatDisabled ?? true;
		const platformChatSupported = isPlatformChatSupported(props.screenshots?.[0]);
		return classroomChatDisabled || !platformChatSupported;
	}, [props.chatDisabled, props.screenshots]);

	useEffect(() => {
		return () => {
			cleanup();
		};
	}, []);

	const handleClickOutsideChat = async () => {
		if (showChat && !useStore.getState().chat.chatToggleModalOpen) {
			toggleShowChat(false);
		}
	};

	useClickOutside([liveChatWrapperRef, chatButtonWrapperRef], handleClickOutsideChat);

	// This useEffect function listens for changes to p2p.peerConnection
	// in order to set up datachannels and such when the conenction is
	// created.
	useEffect(() => {
		if (dataChannelRef.current != null) {
			return;
		}

		if (p2p.peerConnection == null) return;

		const dcConfig = {
			// disabling maxRetransmits temporarily due to an issue introduced in chrome 92 that breaks
			// the datachannel
			// maxRetransmits: 1,
			ordered: false,
			maxPacketLifeTime: 5000,
		};

		if (p2p?.peerConnection?.pc === undefined) {
			return;
		}

		p2p.peerConnection.pc.addEventListener("connectionstatechange", async (event) => {
			switch (event.target.connectionState) {
				case "connected": {
					// log timings for first connection state
					if (!debugFirstConnectedTimestampRef.current) {
						debugFirstConnectedTimestampRef.current = Date.now();
						const deltaSeconds = (debugFirstConnectedTimestampRef.current - debugStartTimestampRef.current) / 1000;
						const connectionType = await getPeersConnectionType(p2p.peerConnection.pc);
						debugFirstConnectedTypeRef.current = connectionType;
						console.info(`Extension Live View - RTCPeerConnection state change: connected (first). sinceStart=${deltaSeconds}`);
						datadogLogs.logger.info(`Extension Live View - RTCPeerConnection state change: connected (first). sinceStart=${deltaSeconds}`, {
							sinceStart: deltaSeconds,
							teacherCandidateType: connectionType.local,
							studentCandidateType: connectionType.remote,
						});
					} else {
						// log subsequent connect state after first
						console.info("Extension Live View - RTCPeerConnection state change: connected");
						datadogLogs.logger.info("Extension Live View - RTCPeerConnection state change: connected");
					}
					break;
				}
				case "disconnected": {
					console.info("Extension Live View - RTCPeerConnection state change: disconnected");
					datadogLogs.logger.info("Extension Live View - RTCPeerConnection state change: disconnected");
					break;
				}
				case "closed": {
					console.info("Extension Live View - RTCPeerConnection state change: closed");
					datadogLogs.logger.info("Extension Live View - RTCPeerConnection state change: closed");
					break;
				}
				case "failed": {
					console.error(`Extension Live View - RTCPeerConnection state change: failed`);
					break;
				}
				default: {
					/* empty */
				}
			}
		});

		p2p.peerConnection.pc.addEventListener("icecandidateerror", (event) => {
			console.log(
				`Extension Live View - RTCPeerConnection ice candidate error: ${event.errorText}, errorCode: ${event.errorCode}, url: ${event.url}`
			);
		});

		p2p.peerConnection.pc.addEventListener("iceconnectionstatechange", (event) => {
			console.log(`Extension Live View - RTCPeerConnection ice connection state change: ${event.target.iceConnectionState}`);
			datadogLogs.logger.log(`Extension Live View - RTCPeerConnection ice connection state change: ${event.target.iceConnectionState}`);
		});

		p2p.peerConnection.pc.addEventListener("signalingstatechange", (event) => {
			console.log(`Extension Live View - RTCPeerConnection signaling state change: ${event.target.signalingState}`);
			datadogLogs.logger.log(`Extension Live View - RTCPeerConnection signaling state change: ${event.target.signalingState}`);
		});

		const controlMessageChannel = p2p.peerConnection.pc.createDataChannel("control");
		const dataChannel = p2p.peerConnection.pc.createDataChannel("main", dcConfig);

		const controlDataChannelWrapper = new DataChannelWrapper(controlMessageChannel, messageReceived);
		const dataChannelWrapper = new DataChannelWrapper(dataChannel, messageReceived);

		dataChannelRef.current = dataChannel;
		dataChannel.addEventListener("open", () => {
			// log timings for first data channel open state
			if (!debugFirstScreenshotDataChannelOpenTimestampRef.current) {
				debugFirstScreenshotDataChannelOpenTimestampRef.current = Date.now();
				const startDeltaSeconds = (debugFirstScreenshotDataChannelOpenTimestampRef.current - debugStartTimestampRef.current) / 1000;
				const connectDeltaSeconds =
					(debugFirstScreenshotDataChannelOpenTimestampRef.current - debugFirstConnectedTimestampRef.current) / 1000;
				console.log(
					`Extension Live View - datachannel state change: open (first). sinceStart=${startDeltaSeconds} sinceConnected=${connectDeltaSeconds}`
				);
				datadogLogs.logger.log(
					`Extension Live View - datachannel state change: open (first). sinceStart=${startDeltaSeconds} sinceConnected=${connectDeltaSeconds}`,
					{ sinceStart: startDeltaSeconds, sinceConnected: connectDeltaSeconds }
				);
			} else {
				// log subsequent data channel open state after first
				console.log(`Extension Live View - datachannel state change: open`);
				datadogLogs.logger.log(`Extension Live View - datachannel state change: open`);
			}
		});
		dataChannel.addEventListener("error", dcErrorUpdate);

		controlDataChannelRef.current = controlMessageChannel;
		controlMessageChannel.addEventListener("open", () => {
			sendNewTabSelection(currentTabRef.current);

			if (!debugFirstControlDataChannelOpenTimestampRef.current) {
				// log timings for first control data channel open state
				debugFirstControlDataChannelOpenTimestampRef.current = Date.now();
				const deltaStartSeconds = (debugFirstControlDataChannelOpenTimestampRef.current - debugStartTimestampRef.current) / 1000;
				const deltaConnectSeconds = (debugFirstControlDataChannelOpenTimestampRef.current - debugFirstConnectedTimestampRef.current) / 1000;
				console.log(
					`Extension Live View - control datachannel state change: open (first). sinceStart=${deltaStartSeconds} sinceConnect=${deltaConnectSeconds}`
				);
				datadogLogs.logger.log(
					`Extension Live View - control datachannel state change: open (first). sinceStart=${deltaStartSeconds} sinceConnect=${deltaConnectSeconds}`,
					{ sinceStart: deltaStartSeconds, sinceConnect: deltaConnectSeconds }
				);
			} else {
				// log subsequent control data channel open state after first
				console.log(`Extension Live View - control datachannel state change: open`);
				datadogLogs.logger.log(`Extension Live View - control datachannel state change: open`);
			}
		});

		controlMessageChannel.addEventListener("error", (event) => {
			console.error(`Extension Live View - control datachannel error event, error message: '${event.error.message}'`);
		});

		dataChannelCloseRef.current = () => {
			dataChannelWrapper.removeListeners();
			dataChannel.removeEventListener("error", dcErrorUpdate);
			dataChannel.close();

			dataChannelRef.current = null;
			dataChannelCloseRef.current = null;
		};

		controlDataChannelCloseRef.current = () => {
			controlDataChannelWrapper.removeListeners();
			controlMessageChannel.removeEventListener("error", dcErrorUpdate);
			controlMessageChannel.close();

			controlDataChannelRef.current = null;
			controlDataChannelCloseRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [p2p.peerConnection]);

	const messageReceived = (message) => {
		switch (message.type) {
			case "screenshot":
				if (message.tab_id === currentTabRef.current.tab_id) {
					setImgDataUri(message.screenshot);
					setScreenshotAvailable(true);
					setScreenshottable(true);
					setScreenshotState(ClassroomStudentActivityState.SCREEN_AVAILABLE);
					setErrorLabel(null);

					// Note: time in transit is an unreliable measurement since it depends on the clock on the sender and receiver being in sync
					const transitTime = (Date.now() - message.timestamp) / 1000;

					if (!debugFirstScreenshotReceivedTimestampRef.current) {
						// log first screenshot
						const now = Date.now();
						debugFirstScreenshotReceivedTimestampRef.current = now;
						const deltaStartSeconds = (now - debugStartTimestampRef.current) / 1000;
						const deltaDcOpenSeconds = (now - debugFirstScreenshotDataChannelOpenTimestampRef.current) / 1000;
						console.log(
							`Extension Live View - first screenshot received. sinceStart=${deltaStartSeconds} sinceDataChannelOpen=${deltaDcOpenSeconds}`
						);
						datadogLogs.logger.log(
							`Extension Live View - first screenshot received. sinceStart=${deltaStartSeconds} sinceDataChannelOpen=${deltaDcOpenSeconds}`,
							{
								sinceStart: deltaStartSeconds,
								sinceDataChannelOpen: deltaDcOpenSeconds,
							}
						);
					} else {
						// log screenshots after first
						console.log(`Extension Live View - screenshot received - time in transit: ${transitTime} seconds (measurement unreliable)`);
					}
				}
				break;

			case "screenshot_unavailable":
				setImgDataUri(null);
				setScreenshotAvailable(false);
				setScreenshottable(true);
				setScreenshotState(ClassroomStudentActivityState.SCREEN_CAPTURE_DISALLOWED);
				break;

			case "tab_update":
				if (message["tabs"].length !== 0) {
					onTabsUpdated(message["tabs"]);
				} else {
					// find another window with same chromeID and connect to its active tab
					const otherWindows = props.user.screenshots.filter(
						(screen) => screen.chrome_id === props.peerAgentId && screen.chrome_window_id !== props.windowId
					);
					if (otherWindows.length > 0) {
						const selected = otherWindows[0];
						sendNewTabSelection(selected);
					}
				}
				break;
			default:
				break;
		}
	};

	const resolveP2pError = useCallback(
		(err) => {
			if (!err) {
				return null;
			}
			if (P2P_ERROR_KEYS.has(err)) {
				return t(`p2pDisplay.${err}`);
			}
			return err;
		},
		[t]
	);

	const getError = () => {
		return resolveP2pError(errorLabel ?? p2p.error);
	};

	const onTabsUpdated = (newTabs) => {
		setTabs(newTabs);

		/// update the tab index to match the tab that is currently selected (by tab id)
		/// this prevents inconsistency when tabs are added / deleted / shifted around
		let newTabIndex = null;
		for (let i = 0; i < newTabs.length; i++) {
			const tab = newTabs[i];
			if (tab.active === true) {
				setActiveTabIndex(i);
				if (followActiveTabRef.current && currentTabRef.current.tab_id !== tab.tab_id) {
					currentTabRef.current = tab;
					setCurrentTab(tab);
					setCurrentTabIndex(i);
					sendNewTabSelection(tab);
					newTabIndex = i;
					break;
				}
			}
			if (tab.tab_id === currentTabRef.current.tab_id) {
				newTabIndex = i;
				setCurrentTabIndex(i);
			}
		}
		// if the current tab was removed, we find a new one to select
		if (newTabIndex === null) {
			let newTab;
			if (currentTabIndexRef.current < newTabs.length) {
				newTab = newTabs[currentTabIndexRef.current];
			} else {
				setCurrentTabIndex(newTabs.length - 1);
				newTab = newTabs[newTabs.length - 1];
			}
			setCurrentTab(newTab);
			currentTabRef.current = newTab;
			sendNewTabSelection(newTab);
		}
	};

	useEffect(() => {
		// follow active tab by default if the teacher selects the active screen to open this modal.
		setFollowActiveTab(props.followActiveTab);
	}, [props.open, props.followActiveTab]);

	const dcErrorUpdate = (event) => {
		if (event) {
			console.error(`Extension Live View - datachannel error event, error message: '${event.error.message}'`);
		}

		if (!dataChannelRef.current) {
			// this shouldn't happen, safety first
			return;
		}

		switch (dataChannelRef.current.readyState) {
			case "connecting": {
				break;
			}
			case "closed":
			case "closing":
				setErrorLabel("datachannelClosed");
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		currentTabRef.current = currentTab;
		currentTabIndexRef.current = currentTabIndex;
		tabsRef.current = tabs;
		followActiveTabRef.current = followActiveTab;
	}, [currentTab, currentTabIndex, tabs, followActiveTab]);

	const setTab = (tabIndex) => {
		const newTab = tabs[tabIndex];

		if (followActiveTabRef.current && !newTab.active) {
			setFollowActiveTab(false);
		}
		sendNewTabSelection(newTab);
		setCurrentTab(newTab);
		setCurrentTabIndex(tabIndex);
		currentTabRef.current = newTab;
		currentTabIndexRef.current = tabIndex;

		setScreenshotAvailable(false);
		setImgDataUri(null);
	};

	const sendNewTabSelection = (tab) => {
		if (tab && tab.tab_id) {
			if (controlDataChannelRef.current === undefined || controlDataChannelRef.current.readyState !== "open") {
				dcErrorUpdate();
				return;
			}
			const [windowId, tabId] = tab.tab_id.split("_");
			controlDataChannelRef.current.send(JSON.stringify({ tabId: parseInt(tabId), windowId: parseInt(windowId) }));
		} else {
			console.error("Something went wrong in the P2P modal: unknown tab");
		}
		setScreenshotState(ClassroomStudentActivityState.UNSET);
	};

	const getCurrentTab = () => {
		return tabsRef.current[currentTabIndexRef.current];
	};

	const handleClickRuleButton = () => {
		const tab = getCurrentTab();
		const websiteUrl = tab["url"] || tab["tabUrl"];
		const signature = convertWebsiteToSignature(websiteUrl) || "";
		const url = signature ? "" : websiteUrl;
		props.openRuleModal(false, url, signature, props.user.username);
	};

	const handleClose = () => {
		// clear selected thread
		useStore.getState().chat.toggleChatWindow(true);
		props.close();
	};

	const onClickSnapshotButton = () => {
		if (snapshotDialogueOpen) {
			setSnapshotDialogueOpen(false);
			return;
		}

		const { active_device, active_username, deviceinfo } = useUserSessionStore.getState();
		const snapshot = screenshotAvailable ? imgDataUri : props.activeTab.screenshot;
		const date = new Date();
		const studentName = userDisplayText(props.user);
		const dateString = `${date.getFullYear()}${date.getMonth()}${date.getDate()}`;
		const timeString = `${date.getHours()}${date.getMinutes()}${date.getSeconds()}`;
		const niceTimeString = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

		const useSmoothwallBranding = useUserSessionStore.getState().deviceinfo.customer_internal_company === "smoothwall";

		const snapshotData = {
			image: snapshot,
			timestamp: `${date.toLocaleString("default", {
				month: "long",
			})} ${date.getDate()} ${date.getFullYear()}, ${niceTimeString}`,
			filename: `${studentName.replace(/\s/g, "")}_${dateString}_${timeString}.pdf`,
			deviceName: deviceinfo?.user_defined_name || active_device,
			deviceId: active_device,
			class: props.classroom.name,
			teacher: active_username,
			studentName: studentName,
			studentUsername: props.user.username,
			platform: props.platform,
			url: currentTab.tabUrl ? currentTab.tabUrl : t("p2pDisplay.missingUrl"),
			smoothwall: useSmoothwallBranding,
			pdfLabels: {
				screenshotTaken: t("p2pDisplay.pdfScreenshotTaken"),
				device: t("p2pDisplay.pdfDevice"),
				teacher: t("p2pDisplay.pdfTeacher"),
				class: t("p2pDisplay.pdfClass"),
				mac: t("p2pDisplay.pdfMac"),
				pageNum: t("p2pDisplay.pdfPageNum"),
			},
		};

		const agreeTime = localStorage.getItem(SCREENSHOT_ACCEPT_LOCALSTORAGE_KEY);
		// if the user has agreed to the download in the last 24 hours, automatically download the PDF
		if (agreeTime && Date.now() / 1000 < parseFloat(agreeTime) + 86400) {
			pdf(<PDFSnapshotDocument {...snapshotData} />)
				.toBlob()
				.then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = snapshotData.filename;
					a.click();
				});
			setSnapshotDialogueOpen(false);
			return;
		}
		setSnapshotDialogueOpen(true);
		setStoredSnapshot(snapshotData);
	};

	const screenshotAccepted = () => {
		localStorage.setItem(SCREENSHOT_ACCEPT_LOCALSTORAGE_KEY, Date.now() / 1000);
	};

	const setScreenshotPopupOpen = (val) => {
		setSnapshotDialogueOpen(val);
	};

	const renderButtons = () => {
		return (
			<Flex gap={2}>
				{!props.isMonitoringClass && props.isStudentClaimed && features.includes("policies") && !props.studentIsFocused && (
					<Button onClick={handleClickRuleButton} gap={2}>
						<ChakraFAIcon icon={"fa-pencil"} variant="solid" />
						{t("p2pDisplay.createRule")}
					</Button>
				)}
				<ConfirmScreenshotPopup
					tooltipOpen={snapshotDialogueOpen}
					filename={storedSnapshot?.filename}
					document={storedSnapshot && <PDFSnapshotDocument {...storedSnapshot} />}
					onAccept={screenshotAccepted}
					onPopupVisibleChange={setScreenshotPopupOpen}
				>
					<Button
						aria-describedby="download-screenshot-confirmation"
						onClick={onClickSnapshotButton}
						isDisabled={snapshotDialogueOpen || !screenshotAvailable}
						gap={2}
					>
						<ChakraFAIcon icon={"fa-camera"} variant="solid" />
						{t("p2pDisplay.screenshot")}
					</Button>
				</ConfirmScreenshotPopup>
				<Button onClick={() => setFullscreenMode(!fullscreenMode)} gap={2}>
					<ChakraFAIcon icon={"fa-up-right-and-down-left-from-center"} variant="solid" />
					{fullscreenMode ? t("p2pDisplay.exitFullScreen") : t("p2pDisplay.fullScreen")}
				</Button>
			</Flex>
		);
	};

	const renderScreen = () => {
		return (
			<div className={fullscreenMode ? "P2PDisplayModal_screenBox--fullscreen" : "P2PDisplayModal_screenBox"}>
				{screenshotAvailable ? (
					<div className="P2PDisplayModal_imgWrap">
						<img
							className={clsx(fullscreenMode && "P2PDisplayModal_fullscreenImage")}
							ref={imgRef}
							src={imgDataUri}
							alt={t("p2pDisplay.screenAlt", { username: props.user.username })}
						/>
					</div>
				) : (
					<ContextBasedP2PWindow
						window={props.activeTab}
						fullscreen={fullscreenMode}
						username={props.user.username}
						currentTab={currentTabRef.current}
						screenshottable={screenshottable}
						imageData={imgDataUri}
						screenshotState={screenshotState}
					/>
				)}
			</div>
		);
	};

	const toggleFollowActiveTab = () => {
		setFollowActiveTab(!followActiveTab);
	};

	if (!props.open) {
		return null;
	}

	const userTitle = t("p2pDisplay.userScreenTitle", { name: userDisplayText(props.user) });

	const handleChatButtonClick = async () => {
		if (!showChat) {
			await useStore.getState().chat.setThreadDetails({
				selectedThreadKey: chat.selectedThreadKey,
				selectedClassId: props.classroom.name,
				selectedStudentName: props.user.username,
			});
		} else {
			useStore.getState().chat.toggleChatWindow(true);
		}

		toggleShowChat(!showChat);
	};

	return (
		<>
			<P2PScreenWrapper
				handleClose={handleClose}
				open={props.open}
				fullscreen={fullscreenMode}
				liveViewReady={screenshotAvailable}
				setFullscreenMode={setFullscreenMode}
				title={userTitle}
				exitButton={
					<div className={"P2PDisplayModal_fullscreenExitButton"} onClick={() => setFullscreenMode(false)}>
						<img src={CloseFullscreenIcon} className="P2PDisplayModal_fullscreenExitButtonIcon" alt={t("p2pDisplay.fullscreenExitAlt")} />
					</div>
				}
			>
				{fullscreenMode ? (
					renderScreen()
				) : (
					<div className={clsx("P2PDisplayModal_content", showChat && "P2PDisplayModal_content__chatOpen")}>
						<div className="P2PDisplayModal_titleContainer">
							<span className="P2PDisplayModal_title">{userTitle}</span>
							{isUserPaused && <span className="P2PDisplayModal_internetPaused"> {t("p2pDisplay.internetPaused")}</span>}
							<label className="P2PDisplayModal_followActiveTabToggleLabel">{t("p2pDisplay.followActiveTab")}</label>
							<Toggle onChange={toggleFollowActiveTab} checked={followActiveTab} />
							{getError() ? <span className="P2PDisplayModal_label--error">{getError()}</span> : null}
							<Flex gap={2}>
								{!props.isMonitoringClass && props.isStudentClaimed && features.includes("open_tab") && (
									<ActionButton
										icon={"fa-browser"}
										label={t("classTools.openTab")}
										testId="open-tab-button"
										handleClick={() => props.showOpenTabModal(props.user)}
										disabled={props.studentIsFocused}
										borderColour={qoriaColours.brand.neutral[200]}
									/>
								)}
								{!props.isMonitoringClass && props.isStudentClaimed && features.includes("message_student") && (
									<ActionButton
										icon="fa-bullhorn"
										label={t("p2pDisplay.sendMessage")}
										testId="liveview-send-message-btn"
										handleClick={() => setSendMessageModalOpen(true)}
										borderColour={qoriaColours.brand.neutral[200]}
									/>
								)}
								{!props.isMonitoringClass && props.isStudentClaimed && chat.isChatEnabled && !disabledChat && (
									<div ref={chatButtonWrapperRef}>
										<ChatButton
											handleClick={handleChatButtonClick}
											hasUnreadMessages={chat.hasUnreadMessages}
											hasActiveClass
											borderColour={qoriaColours.brand.neutral[200]}
										/>
									</div>
								)}
							</Flex>
						</div>
						<div className="P2PDisplayModal_screenAndChatContainer">
							<div className="P2PDisplayModal_screenContainer">
								<P2PTabsBar
									user={props.user}
									activeTab={activeTabIndex}
									tabs={tabsRef.current}
									selectTab={setTab}
									selectedTab={followActiveTab ? activeTabIndex : currentTabIndex}
									closeTab={props.closeTab}
									canCloseTab={features.includes("close_tab")}
									isMonitoringClass={props.isMonitoringClass}
									isStudentClaimed={props.isStudentClaimed}
								/>
								{renderScreen()}
							</div>
							<div
								className={clsx("P2PDisplayModal_chatContainer", showChat && "P2PDisplayModal_chatContainer--visible")}
								ref={liveChatWrapperRef}
							>
								{showChat && (
									<ChatWindow
										classroom={props.classroom}
										isChatWorking={!!useStore.getState().chat.unreadMessagesCount.data}
										handleClose={toggleShowChat}
										isEmbedded
									/>
								)}
							</div>
						</div>
						<Flex justifyContent={"space-around"}>{renderButtons()}</Flex>
					</div>
				)}
			</P2PScreenWrapper>
			<SendMessageModal
				open={sendMessageModalOpen}
				title={sendMessageModalTitle}
				classroomSourceId={classroomSourceId}
				users={[props.user]}
				onClose={() => setSendMessageModalOpen(false)}
				subtitle={t("classroomDetails.studentMessageSubtitle")}
			/>
		</>
	);
};

P2PDisplayModal.propTypes = {
	user: PropTypes.object,
	activeTab: PropTypes.object,
	closeTab: PropTypes.func,
	isMonitoringClass: PropTypes.bool,
	isStudentClaimed: PropTypes.bool,
	open: PropTypes.bool,
	close: PropTypes.func,
	openRuleModal: PropTypes.func,
	startTabIndex: PropTypes.number,
	peerAgentId: PropTypes.string,
	windowId: PropTypes.string,
	startTab: PropTypes.object,
	tabs: PropTypes.array,
	screenshots: PropTypes.array,
	classroom: PropTypes.object,
	chatDisabled: PropTypes.bool,
	showOpenTabModal: PropTypes.func,
	followActiveTab: PropTypes.bool,
	platform: PropTypes.string,
	studentIsFocused: PropTypes.bool,
};

export default P2PDisplayModal;
